// @ts-nocheck
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface EventInquiryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EventInquiryModal: React.FC<EventInquiryModalProps> = ({
  open,
  onOpenChange
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const resetForm = () => {
    setName('');
    setEmail('');
    setOrganization('');
    setPhone('');
    setMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Insert inquiry into database
      const { data, error } = await supabase
        .from('event_management_inquiries')
        .insert({
          name: name.trim(),
          email: email.trim(),
          organization: organization.trim() || null,
          phone: phone.trim() || null,
          message: message.trim()
        })
        .select()
        .single();

      if (error) throw error;

      // Send notification emails via edge function
      try {
        const { data: functionData, error: functionError } = await invokeEdgeFunction('notify-admins-event-inquiry', {
          body: {
            name: name.trim(),
            email: email.trim(),
            organization: organization.trim() || undefined,
            phone: phone.trim() || undefined,
            message: message.trim(),
            inquiryId: data.id
          }
        });

        if (functionError) {
          console.error('Failed to send notification emails:', functionError);
        } else {
          console.log('Notification emails sent successfully:', functionData);
        }
      } catch (notifyError) {
        console.error('Error sending notification emails:', notifyError);
        // Don't fail the submission if email notification fails
      }

      toast({
        title: "Inquiry submitted!",
        description: "Thank you for your interest in our event management services. Our team will contact you soon!",
      });
      
      resetForm();
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting event inquiry:', error);
      toast({
        title: "Something went wrong",
        description: "We couldn't submit your inquiry. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-full">
        <DialogHeader>
          <DialogTitle className="text-xl font-heading">Get Started with Event Management</DialogTitle>
          <DialogDescription className="pt-2">
            Ready to organize world-class sporting events? Tell us about your needs and our event management specialists will contact you.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input 
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input 
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="organization">Organization</Label>
            <Input 
              id="organization"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              placeholder="Your organization or company"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input 
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Your phone number"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="message">Tell us about your event *</Label>
            <Textarea 
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe your event, expected number of participants, timeline, and any specific requirements..."
              className="min-h-[120px] resize-none"
              required
            />
          </div>
          
          <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button 
              variant="outline" 
              type="button" 
              onClick={() => onOpenChange(false)}
              className="sm:ml-2"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting || !name.trim() || !email.trim() || !message.trim()}
              className="bg-secondary hover:bg-secondary-600 text-slate-900 font-black"
            >
              {isSubmitting ? "Submitting..." : "Submit Inquiry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EventInquiryModal;